import React, { useState, useEffect } from 'react';
import './PhotoViewer.css';
import { BACKEND_ROOT } from '../../config';

const PhotoViewer = ({ photos, currentPhotoIndex, onClose }) => {
  const [photoIndex, setPhotoIndex] = useState(currentPhotoIndex || 0);
  
  useEffect(() => {
    setPhotoIndex(currentPhotoIndex || 0);
  }, [currentPhotoIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && photoIndex > 0) {
        setPhotoIndex(photoIndex - 1);
      } else if (e.key === 'ArrowRight' && photoIndex < photos.length - 1) {
        setPhotoIndex(photoIndex + 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [photoIndex, photos.length, onClose]);

  if (!photos || photos.length === 0) return null;

  const currentPhoto = photos[photoIndex];
  if (!currentPhoto) return null;

  const getPhotoSrc = (photo) => {
    // Handle building photos with different data structures
    
    // First check for file_path from database (media files)
    if (photo.file_path) {
      const mediaPath = photo.file_path.replace('/app/media/', '');
      return `${BACKEND_ROOT}/media/${mediaPath}`;
    }
    
    if (photo.data && photo.data.startsWith('data:')) return photo.data;
    if (photo.data && !photo.data.startsWith('http')) {
      // Raw base64 data, need to add data URL prefix
      const mimeType = photo.mimeType || photo.mime_type || 'image/jpeg';
      return `data:${mimeType};base64,${photo.data}`;
    }
    if (photo.data) return photo.data;
    if (photo.url) return photo.url;
    if (photo.src) return photo.src;
    if (photo.objectUrl) return photo.objectUrl;
    return null;
  };

  const getFileExtension = (photo) => {
    if (photo.fileName) {
      const ext = photo.fileName.split('.').pop();
      return ext ? `.${ext.toUpperCase()}` : '';
    }
    if (photo.originalName) {
      const ext = photo.originalName.split('.').pop();
      return ext ? `.${ext.toUpperCase()}` : '';
    }
    if (photo.editable_filename) {
      const ext = photo.editable_filename.split('.').pop();
      return ext ? `.${ext.toUpperCase()}` : '';
    }
    if (photo.structured_filename) {
      const ext = photo.structured_filename.split('.').pop();
      return ext ? `.${ext.toUpperCase()}` : '';
    }
    if (photo.mimeType || photo.mime_type) {
      const mimeType = photo.mimeType || photo.mime_type;
      if (mimeType.includes('jpeg')) return '.JPEG';
      if (mimeType.includes('jpg')) return '.JPG';
      if (mimeType.includes('png')) return '.PNG';
      if (mimeType.includes('gif')) return '.GIF';
      if (mimeType.includes('webp')) return '.WEBP';
    }
    return '.JPG';
  };

  const handlePrevious = () => {
    if (photoIndex > 0) {
      setPhotoIndex(photoIndex - 1);
    }
  };

  const handleNext = () => {
    if (photoIndex < photos.length - 1) {
      setPhotoIndex(photoIndex + 1);
    }
  };

  return (
    <div className="photo-viewer-overlay">
      {/* Main photo container - always centered */}
      <div className="photo-viewer-container">
        {/* Close button */}
        <button
          onClick={onClose}
          className="photo-viewer-button bg-black bg-opacity-70 text-white hover:bg-opacity-90 rounded-full w-8 h-8 flex items-center justify-center text-xl font-light"
          style={{ 
            fontFamily: 'Arial, sans-serif',
            top: '0.5rem',
            right: '0.5rem'
          }}
          title="Schließen"
        >
          ×
        </button>

        {/* File extension indicator */}
        <div 
          className="photo-viewer-button bg-black bg-opacity-70 text-white text-xs font-medium px-2 py-1 rounded"
          style={{
            top: '0.5rem',
            left: '0.5rem'
          }}
        >
          {getFileExtension(currentPhoto)}
        </div>

        {/* Photo counter */}
        {photos.length > 1 && (
          <div 
            className="photo-viewer-button bg-black bg-opacity-70 text-white text-xs font-medium px-3 py-1 rounded"
            style={{
              top: '0.5rem',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            {photoIndex + 1} / {photos.length}
          </div>
        )}

        {/* Navigation arrows */}
        {photos.length > 1 && (
          <>
            {photoIndex > 0 && (
              <button
                onClick={handlePrevious}
                className="photo-viewer-button bg-black bg-opacity-70 text-white hover:bg-opacity-90 rounded-full w-10 h-10 flex items-center justify-center text-2xl font-light"
                style={{ 
                  fontFamily: 'Arial, sans-serif',
                  left: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
                title="Vorheriges Foto"
              >
                ‹
              </button>
            )}
            {photoIndex < photos.length - 1 && (
              <button
                onClick={handleNext}
                className="photo-viewer-button bg-black bg-opacity-70 text-white hover:bg-opacity-90 rounded-full w-10 h-10 flex items-center justify-center text-2xl font-light"
                style={{ 
                  fontFamily: 'Arial, sans-serif',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
                title="Nächstes Foto"
              >
                ›
              </button>
            )}
          </>
        )}

        {/* Photo */}
        <img
          src={getPhotoSrc(currentPhoto)}
          alt={currentPhoto.fileName || currentPhoto.originalName || currentPhoto.editable_filename || 'Foto'}
          className="photo-viewer-image"
          onError={(e) => {
            console.error('Error loading photo:', currentPhoto);
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>

      {/* Click outside to close - positioned behind */}
      <div 
        className="photo-viewer-backdrop"
        onClick={onClose}
      />
    </div>
  );
};

export default PhotoViewer;
